import { Lock } from "lucide-react";

interface PriorityReadOnlyNoticeProps {
    /** DRI's display name, when the task has one. */
    driName?: string;
    /** True when the task has no DRI at all, so there is nobody to ask. */
    isDriMissing: boolean;
    /** True when the current user has their own assignment row, which stays editable right here. */
    canEditOwnRow?: boolean;
}

/**
 * Says why the task-level fields are read-only, before the user tries to change something.
 *
 * Two wordings, because the two cases have different next steps: a DRI exists and can be asked, or the
 * task has none and needs one set. Telling someone to ask a DRI that does not exist is a dead end — the
 * same reason the backend gives these two refusals separate error codes.
 *
 * When the user has their own assignment row, the notice ends by naming what they *can* still do — edit
 * that row right here. A permission notice with no alternative reads as a bug rather than a rule.
 */
export function PriorityReadOnlyNotice({ driName, isDriMissing, canEditOwnRow = false }: PriorityReadOnlyNoticeProps) {
    return (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-500" aria-hidden />
            <p className="text-muted-foreground">
                {isDriMissing ? (
                    <>
                        <strong className="font-medium text-foreground">This task has no DRI yet.</strong> Claim it as
                        DRI to take it on, or ask its creator or an admin to edit it.
                    </>
                ) : (
                    <>
                        <strong className="font-medium text-foreground">
                            {driName ? `${driName} is the DRI` : "Only the DRI"}
                        </strong>{" "}
                        {driName ? "and owns" : "owns"} this task&rsquo;s details, status, assignees and DRI.
                    </>
                )}
                {canEditOwnRow ? (
                    <>
                        {" "}
                        You can still edit{" "}
                        <strong className="font-medium text-foreground">your own status and IC task</strong> below.
                    </>
                ) : null}
            </p>
        </div>
    );
}
